import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Bell, Mail, BarChart3, Search, Smartphone, Moon, Sparkles, Megaphone } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import {
  isPushSupported,
  getCurrentPermission,
  promptForPushIfMeaningful,
  subscribeToPush,
  unsubscribeFromPush,
} from '../utils/pushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PREF_CONFIG = [
  { key: 'message_notifications', icon: Mail, labelAr: 'إشعارات الرسائل', labelEn: 'Message notifications' },
  { key: 'ad_performance', icon: BarChart3, labelAr: 'ملخص أداء الإعلانات', labelEn: 'Ad performance summary' },
  { key: 'new_ads_alerts', icon: Bell, labelAr: 'تنبيهات الإعلانات الجديدة', labelEn: 'New ads alerts' },
  { key: 'saved_search_alerts', icon: Search, labelAr: 'تنبيهات البحث المحفوظ', labelEn: 'Saved search alerts' },
];

export default function NotificationPreferences() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { token, user } = useAuth();
  const isRTL = i18n.language?.startsWith('ar');
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);

  // Push-notification opt-in state.
  // We track BOTH the OS-level browser permission AND whether we currently
  // have an active local PushSubscription. Toggling subscribe/unsubscribe
  // does NOT change OS permission; once the user has granted at the
  // browser level, future on/off taps should not need to re-prompt.
  const [pushSupported] = useState(() => isPushSupported());
  const [pushPermission, setPushPermission] = useState(() => getCurrentPermission());
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  // Read the live SW subscription state on mount + whenever permission flips.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!pushSupported) return;
      try {
        const reg = await navigator.serviceWorker.getRegistration('/');
        if (cancelled) return;
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (cancelled) return;
        setPushSubscribed(!!sub);
      } catch { /* keep subscribed=false */ }
    })();
    return () => { cancelled = true; };
  }, [pushSupported, pushPermission]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/users/me/notification-preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setPrefs(d.preferences || {}); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  // Trigger #2 (foundation): opening this page is itself the meaningful
  // action. The helper is one-shot guarded by localStorage so it only ever
  // shows the browser permission prompt the very first time the user
  // visits notification settings — exactly per spec, no auto-prompt
  // elsewhere in the app.
  useEffect(() => {
    if (!pushSupported || !user?.user_id) return;
    if (getCurrentPermission() !== 'default') return; // already answered
    promptForPushIfMeaningful({
      userId: user.user_id,
      token,
      language: isRTL ? 'ar' : 'en',
    })
      .then((res) => {
        setPushPermission(getCurrentPermission());
        if (res && res.granted && res.subscribed && res.subscribed.ok) {
          setPushSubscribed(true);
        }
      })
      .catch(() => { /* never block UI */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, pushSupported]);

  async function toggle(key) {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    try {
      await fetch(`${API_URL}/api/users/me/notification-preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [key]: updated[key] }),
      });
    } catch { /* non-blocking */ }
  }

  // Persist a non-boolean preference (digest freq, timezone, etc.).
  async function setStringPref(key, value) {
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);
    try {
      await fetch(`${API_URL}/api/users/me/notification-preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [key]: value }),
      });
    } catch { /* non-blocking */ }
  }

  // Persist an integer hour (0..23) preference for quiet-hours window.
  async function setHourPref(key, value) {
    const hr = Math.max(0, Math.min(23, parseInt(value, 10) || 0));
    const updated = { ...prefs, [key]: hr };
    setPrefs(updated);
    try {
      await fetch(`${API_URL}/api/users/me/notification-preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [key]: hr }),
      });
    } catch { /* non-blocking */ }
  }

  async function togglePush() {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      // Currently subscribed → user wants OFF → unsubscribe but keep OS permission.
      if (pushSubscribed) {
        await unsubscribeFromPush({ token });
        setPushSubscribed(false);
        return;
      }

      // Browser explicitly blocked → can't prompt; UI shows the helper note.
      if (pushPermission === 'denied') return;

      // Permission already granted at OS level → subscribe directly, no prompt.
      if (pushPermission === 'granted') {
        const r = await subscribeToPush({
          token,
          language: isRTL ? 'ar' : 'en',
        });
        setPushSubscribed(!!(r && r.ok));
        return;
      }

      // First-time path: ask permission then (on grant) subscribe.
      const res = await promptForPushIfMeaningful({
        userId: user?.user_id,
        token,
        language: isRTL ? 'ar' : 'en',
      });
      setPushPermission(getCurrentPermission());
      setPushSubscribed(!!(res && res.granted && res.subscribed && res.subscribed.ok));
    } finally {
      setPushBusy(false);
    }
  }

  return (
    <div className="np-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="notification-preferences-page">
      <AppHeader
        title={isRTL ? 'إعدادات الإشعارات' : 'Notification Settings'}
        backFallback="/settings"
        testId="np-header"
      />

      <p className="np-subtitle">
        {isRTL
          ? 'تحكم في الإشعارات التي تصلك عبر البريد الإلكتروني.'
          : 'Control which email notifications you receive.'}
      </p>

      {loading ? (
        <div className="np-loading">{isRTL ? 'جاري التحميل...' : 'Loading...'}</div>
      ) : (
        <div className="np-list" data-testid="np-list">
          {pushSupported && (
            <div className="np-row" data-testid="np-row-push" key="push">
              <div className="np-row-left">
                <Smartphone size={18} className="np-icon" />
                <span>{isRTL ? 'إشعارات الجهاز' : 'Device push notifications'}</span>
              </div>
              <button
                className={`np-toggle ${pushSubscribed ? 'np-toggle--on' : ''}`}
                onClick={togglePush}
                data-testid="np-toggle-push"
                aria-label="Toggle device push notifications"
                disabled={pushBusy || pushPermission === 'denied'}
              >
                <span className="np-toggle-knob" />
              </button>
            </div>
          )}
          {pushSupported && pushPermission === 'denied' && (
            <div className="np-row np-row--note" data-testid="np-row-push-blocked">
              <span className="np-note-text">
                {isRTL
                  ? 'تم حظر الإشعارات من إعدادات المتصفح. يمكنك تفعيلها يدويًا.'
                  : 'Notifications are blocked in your browser settings. Please enable them there to turn this on.'}
              </span>
            </div>
          )}

          {/* Smart-notification policy controls. These drive the
              notification_policy.should_send gate on the backend —
              frequency caps, quiet hours, channel routing. */}
          <div className="np-section-title" data-testid="np-section-smart">
            {isRTL ? 'الإشعارات الذكية' : 'Smart notifications'}
          </div>

          <div className="np-row" data-testid="np-row-push-personalised">
            <div className="np-row-left">
              <Sparkles size={18} className="np-icon" />
              <span>{isRTL ? 'تنبيهات مخصصة لك' : 'Personalized alerts'}</span>
            </div>
            <button
              className={`np-toggle ${prefs?.push_personalised !== false ? 'np-toggle--on' : ''}`}
              onClick={() => toggle('push_personalised')}
              data-testid="np-toggle-push_personalised"
              aria-label="Toggle personalized push"
            ><span className="np-toggle-knob" /></button>
          </div>

          <div className="np-row" data-testid="np-row-push-generic">
            <div className="np-row-left">
              <Bell size={18} className="np-icon" />
              <span>{isRTL ? 'تحديثات عامة (مرة يوميًا كحد أقصى)' : 'General updates (max 1/day)'}</span>
            </div>
            <button
              className={`np-toggle ${prefs?.push_generic !== false ? 'np-toggle--on' : ''}`}
              onClick={() => toggle('push_generic')}
              data-testid="np-toggle-push_generic"
              aria-label="Toggle generic push"
            ><span className="np-toggle-knob" /></button>
          </div>

          <div className="np-row" data-testid="np-row-push-promo">
            <div className="np-row-left">
              <Megaphone size={18} className="np-icon" />
              <span>{isRTL ? 'العروض والترويج (3 أسبوعيًا كحد أقصى)' : 'Promotions (max 3/week)'}</span>
            </div>
            <button
              className={`np-toggle ${prefs?.push_promotional === true ? 'np-toggle--on' : ''}`}
              onClick={() => toggle('push_promotional')}
              data-testid="np-toggle-push_promotional"
              aria-label="Toggle promotional push"
            ><span className="np-toggle-knob" /></button>
          </div>

          <div className="np-row" data-testid="np-row-quiet-hours">
            <div className="np-row-left">
              <Moon size={18} className="np-icon" />
              <span>{isRTL ? 'ساعات الهدوء' : 'Quiet hours'}</span>
            </div>
            <button
              className={`np-toggle ${prefs?.quiet_hours_enabled !== false ? 'np-toggle--on' : ''}`}
              onClick={() => toggle('quiet_hours_enabled')}
              data-testid="np-toggle-quiet_hours_enabled"
              aria-label="Toggle quiet hours"
            ><span className="np-toggle-knob" /></button>
          </div>

          {prefs?.quiet_hours_enabled !== false && (
            <div className="np-row np-row--inline" data-testid="np-row-quiet-hours-range">
              <span className="np-inline-label">
                {isRTL ? 'من' : 'From'}
              </span>
              <select
                className="np-inline-select"
                value={prefs?.quiet_hours_start ?? 22}
                onChange={(e) => toggle.length && setHourPref('quiet_hours_start', e.target.value)}
                data-testid="np-quiet-start"
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                ))}
              </select>
              <span className="np-inline-label">
                {isRTL ? 'إلى' : 'to'}
              </span>
              <select
                className="np-inline-select"
                value={prefs?.quiet_hours_end ?? 8}
                onChange={(e) => setHourPref('quiet_hours_end', e.target.value)}
                data-testid="np-quiet-end"
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
          )}

          <div className="np-row np-row--inline" data-testid="np-row-email-freq">
            <div className="np-row-left">
              <Mail size={18} className="np-icon" />
              <span>{isRTL ? 'تكرار ملخص البريد' : 'Email digest frequency'}</span>
            </div>
            <select
              className="np-inline-select"
              value={prefs?.email_digest_freq || 'weekly'}
              onChange={(e) => setStringPref('email_digest_freq', e.target.value)}
              data-testid="np-email-freq"
            >
              <option value="off">{isRTL ? 'إيقاف' : 'Off'}</option>
              <option value="daily">{isRTL ? 'يومي' : 'Daily'}</option>
              <option value="weekly">{isRTL ? 'أسبوعي' : 'Weekly'}</option>
            </select>
          </div>

          <div className="np-section-title" data-testid="np-section-email">
            {isRTL ? 'البريد الإلكتروني' : 'Email'}
          </div>

          {PREF_CONFIG.map(({ key, icon: Icon, labelAr, labelEn }) => (
            <div className="np-row" key={key} data-testid={`np-row-${key}`}>
              <div className="np-row-left">
                <Icon size={18} className="np-icon" />
                <span>{isRTL ? labelAr : labelEn}</span>
              </div>
              <button
                className={`np-toggle ${prefs?.[key] !== false ? 'np-toggle--on' : ''}`}
                onClick={() => toggle(key)}
                data-testid={`np-toggle-${key}`}
                aria-label={`Toggle ${key}`}
              >
                <span className="np-toggle-knob" />
              </button>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .np-page { max-width: 500px; margin: 0 auto; padding: 16px; min-height: 100vh; background: var(--mg-bg, #fff); }
        .np-header { display: flex; align-items: center; gap: 12px; margin-bottom: 4px; }
        .np-header h1 { font-size: 18px; font-weight: 700; color: var(--mg-text, #111); margin: 0; }
        .np-back { background: none; border: none; cursor: pointer; padding: 4px; color: var(--mg-text, #111); }
        .np-subtitle { font-size: 14px; color: #6b7280; margin: 0 0 20px; padding: 0 4px; line-height: 1.5; }
        .np-loading { text-align: center; padding: 40px; color: #9ca3af; }
        .np-list { display: flex; flex-direction: column; gap: 2px; background: var(--mg-card-bg, #f9fafb); border-radius: 12px; overflow: hidden; }
        .np-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; background: var(--mg-card-bg, #fff); border-bottom: 1px solid rgba(0,0,0,0.05); }
        .np-row--note { display: block; padding: 10px 16px; background: #FFFBEB; }
        .np-row--inline { gap: 8px; flex-wrap: wrap; }
        .np-inline-label { font-size: 13px; color: #6b7280; }
        .np-inline-select { padding: 6px 10px; border-radius: 8px; border: 1px solid rgba(0,0,0,0.12); background: #fff; font-size: 13px; min-width: 92px; }
        .np-section-title { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #9ca3af; padding: 18px 16px 6px; background: var(--mg-bg, #fff); border-bottom: none; }
        [dir="rtl"] .np-section-title { text-align: right; }
        .np-note-text { font-size: 12px; color: #92400E; line-height: 1.4; }
        [dir="rtl"] .np-note-text { text-align: right; display: block; }
        .np-toggle:disabled { opacity: 0.6; cursor: not-allowed; }
        .np-row:last-child { border-bottom: none; }
        .np-row-left { display: flex; align-items: center; gap: 10px; font-size: 14px; font-weight: 500; color: var(--mg-text, #111); }
        .np-icon { color: #6b7280; flex-shrink: 0; }
        .np-toggle { width: 44px; height: 26px; border-radius: 13px; border: none; cursor: pointer; background: #d1d5db; position: relative; transition: background 0.2s ease; flex-shrink: 0; }
        .np-toggle--on { background: #1E8E5A; }
        .np-toggle-knob { width: 20px; height: 20px; border-radius: 50%; background: #fff; position: absolute; top: 3px; left: 3px; transition: transform 0.2s ease; box-shadow: 0 1px 3px rgba(0,0,0,0.15); }
        .np-toggle--on .np-toggle-knob { transform: translateX(18px); }
        [dir="rtl"] .np-toggle-knob { left: auto; right: 3px; }
        [dir="rtl"] .np-toggle--on .np-toggle-knob { transform: translateX(-18px); }
        html.dark .np-page { background: #0f172a; }
        html.dark .np-list { background: #1e293b; }
        html.dark .np-row { background: #1e293b; border-color: rgba(255,255,255,0.05); }
      `}</style>
    </div>
  );
}
